/**
 * PATH: packages/auth/src/session-manager/silentMasterRefresh.ts
 * FILE_ID: AUTH-SILENT-MASTER-REFRESH-001
 * PURPOSE: Best-effort silent refresh of the master Quotech Auth v2
 *          session for the master SPA's SessionManager.
 *
 * WHY (BUG-2026-05-25-STAY-LOGGED-IN-LOGOUT-AFTER-UPDATES):
 *   Operator-reported (2026-05-25): "yo keep signing me out... especially
 *   after udpates et.c. common man... whawat happened to selecting stay
 *   logged in, it should only log me out if I click log out or loginto
 *   another user".
 *
 *   Root cause: `SessionManager.checkSessionHealth` (this module's caller)
 *   was calling `handleSessionDeath('invalid' | 'session_expired')` the
 *   moment the 60-min JS-readable `quotech_auth` cookie went stale or its
 *   inner JWT passed `exp`, WITHOUT first asking the 30-day HttpOnly
 *   `quotech_refresh` cookie to re-mint a fresh access token. The
 *   tenant-side gate (`createApiAuthGate.attemptQuotechSSO`) already had
 *   this fallback wired via `@quotech/ui/auth/silentRefresh.ts`; the
 *   master-side `SessionManager` did not. Result: every time the user
 *   came back to a tab whose access cookie had aged past 60 min — or
 *   was reloaded by `VersionWatcher` after a deploy — they were bounced
 *   to `/login` even though their 30-day "Stay logged in" refresh cookie
 *   was perfectly alive.
 *
 *   This module mirrors `packages/ui/src/auth/silentRefresh.ts` so the
 *   master SPA gets the SAME silent-refresh path as the tenant SPAs.
 *   Duplicated (not imported) to avoid a circular dep between
 *   `@quotech/auth` and `@quotech/ui` — the factory in `@quotech/ui`
 *   takes its token helpers FROM `@quotech/auth`, so flipping the
 *   direction here would close a circular import.
 *
 * SCOPE:
 *   - Pure helper. No React, no DOM beyond `fetch`. The SessionManager
 *     calls this from `checkSessionHealth` BEFORE falling through to
 *     `handleSessionDeath`.
 *   - Returns true iff the master refresh endpoint responded 200, in
 *     which case `document.cookie` now has a fresh `quotech_auth`
 *     mirror that the caller can re-read via `getAuthCookie()`.
 *   - Never throws — any failure (network, CORS, revoked / expired
 *     refresh cookie) returns false; the caller decides whether to
 *     fall through to v1 refresh or sign the user out.
 *
 * RELATED:
 *   - `packages/ui/src/auth/silentRefresh.ts` — tenant-side twin.
 *     Keep both in lockstep; if the master refresh URL ever moves,
 *     update BOTH constants together.
 *   - `core/settings/base.py:AUTHV2_REFRESH_TTL_SECONDS` — the
 *     30-day TTL we are silently refreshing against.
 *   - `authentication/authv2/views/refresh.py:RefreshV2API` — the
 *     server endpoint this helper hits.
 */

/**
 * Hard-coded master URL — the master backend is the single canonical
 * issuer. We deliberately do NOT make this configurable: a misconfig
 * would silently break the silent-refresh path with no error surface,
 * which is exactly the class of bug this module exists to fix.
 *
 * Same constant as `@quotech/ui/auth/silentRefresh.ts`. Lifted into a
 * named export so a future env-driven override (e.g. for non-prod
 * domains) only has to change here.
 */
export const QUOTECH_AUTHV2_REFRESH_URL =
  'https://dashboard.quotech.ai/api/auth/v2/refresh/'

import type { RefreshOutcome } from './constants'
import {
  clearAuthV2CookieRefreshDead,
  isAuthV2CookieRefreshDead,
  markAuthV2CookieRefreshDead,
} from './authV2CookieRefreshDead'

export interface SilentMasterRefreshDeps {
  /** Test seam: defaults to global `fetch`. */
  fetchImpl?: typeof fetch
  /** Test seam: override the master refresh URL (defaults to
   * `QUOTECH_AUTHV2_REFRESH_URL`). Production NEVER overrides this. */
  refreshUrl?: string
}

/**
 * Cross-package single-flight latch for the master refresh POST.
 *
 * BUG-2026-07-25-AUTHV2-DUPLICATE-BOOT-REFRESH — the master shell fires TWO
 * independent refresh POSTs on every cold load, so the operator's console
 * shows `/api/auth/v2/refresh/ 401` twice:
 *
 *   1. `SessionManager.startMonitoring` runs an immediate health check at
 *      module-evaluation time (main.jsx), which reaches
 *      `attemptSilentMasterRefresh`;
 *   2. the Signin page's silent-resurrection effect fires
 *      `attemptMasterQuotechRefresh()` — the root URL mounts Signin even for
 *      an authenticated user (Pages/Auth/routeGuards.jsx isLoginEntry).
 *
 * Each caller had its own in-flight guard, but they live in DIFFERENT packages
 * (`@quotech/auth` here, `@quotech/ui` in the tenant twin) and therefore in
 * different module instances, so neither could see the other's promise. The
 * latch is parked on a `Symbol.for` slot precisely because it must be shared
 * across package instances without `@quotech/auth` importing `@quotech/ui`
 * (that direction closes the circular import this module was duplicated to
 * avoid).
 *
 * INVARIANT: at most one master refresh POST is in flight per document.
 *
 * Deliberately an in-flight collapse only, NOT a result cache. A cached
 * negative would have to survive across a user's login round-trip, and a
 * stale 'dead' verdict is exactly what signs a just-authenticated user back
 * out (see BUG-2026-06-29-V2-INVALID-HASH-CLEARS-LIVE-BEARER). Collapsing
 * concurrent calls carries no such risk: every joiner gets the verdict of a
 * request that was live at the moment it asked.
 *
 * The key string is duplicated verbatim in `packages/ui/src/auth/silentRefresh.ts`
 * and pinned by `__tests__/masterRefreshSingleFlight.test.ts` — if the two ever
 * drift the latch silently degrades to per-package dedupe and the double POST
 * returns.
 */
const MASTER_REFRESH_LATCH_KEY = Symbol.for('quotech.authv2.master-refresh.inflight')

/** Normalised transport result shared across package instances.
 *
 * Primitive-only on purpose: this module maps it to a `RefreshOutcome` while
 * the `@quotech/ui` twin maps it to a boolean, so the shared value cannot
 * carry either package's verdict type. */
interface MasterRefreshTransportResult {
  ok: boolean
  status: number | null
}

interface MasterRefreshLatch {
  inflight: Promise<MasterRefreshTransportResult> | null
}

function masterRefreshLatch(): MasterRefreshLatch {
  const host = globalThis as unknown as Record<symbol, MasterRefreshLatch | undefined>
  const existing = host[MASTER_REFRESH_LATCH_KEY]
  if (existing) return existing
  const created: MasterRefreshLatch = { inflight: null }
  host[MASTER_REFRESH_LATCH_KEY] = created
  return created
}

/** Test seam: clear the shared in-flight latch between cases. */
export function resetMasterRefreshLatch(): void {
  masterRefreshLatch().inflight = null
}

async function postMasterRefresh(
  fetchImpl: typeof fetch,
  url: string,
): Promise<MasterRefreshTransportResult> {
  try {
    const response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
    return {
      ok: Boolean(response.ok),
      status: typeof response.status === 'number' ? response.status : null,
    }
  } catch {
    // Network error / CORS / abort — the refresh token may still be alive.
    return { ok: false, status: null }
  }
}

/** Runs the POST, collapsing it onto any request already in flight.
 *
 * `deduped` is false whenever the caller injected its own transport: an
 * explicit `fetchImpl` is a caller-controlled seam that must observe exactly
 * the calls it makes. Every production call site passes no deps, so the whole
 * real boot path is deduped. */
async function requestMasterRefresh(
  fetchImpl: typeof fetch,
  url: string,
  deduped: boolean,
): Promise<MasterRefreshTransportResult> {
  if (!deduped) return postMasterRefresh(fetchImpl, url)
  const latch = masterRefreshLatch()
  const joined = latch.inflight
  if (joined) return joined
  const attempt = postMasterRefresh(fetchImpl, url)
  latch.inflight = attempt
  try {
    return await attempt
  } finally {
    if (latch.inflight === attempt) latch.inflight = null
  }
}

/** Best-effort silent refresh of the master Quotech Auth v2 session.
 * Returns true iff the master responded 200, in which case a fresh
 * `quotech_auth` cookie is already in `document.cookie` and the
 * caller can re-read it via `getAuthCookie()`.
 *
 * Never throws — any failure (network, CORS, refresh cookie revoked,
 * refresh cookie expired past 30 days, master backend down) returns
 * false and the caller can decide whether to fall through to v1
 * refresh or sign the user out.
 *
 * Concurrent calls collapse onto one POST via the cross-package latch
 * above (BUG-2026-07-25-AUTHV2-DUPLICATE-BOOT-REFRESH). */
export async function attemptMasterQuotechRefresh(
  deps: SilentMasterRefreshDeps = {},
): Promise<boolean> {
  return (await classifyMasterQuotechRefresh(deps)) === 'refreshed'
}

/** Like {@link attemptMasterQuotechRefresh} but distinguishes a DEFINITIVE
 * failure (the master answered 401/403 — refresh cookie missing / expired /
 * revoked / unknown session) from a TRANSIENT one (network, CORS, 5xx, 429,
 * abort). Only a 'dead' verdict justifies signing the user out; a 'transient'
 * verdict must preserve the session and retry. See
 * BUG-2026-06-04-LOGOUT-EVERY-HOUR and the `RefreshOutcome` doc in
 * `constants.ts`.
 *
 * NOTE: a 401 here means "no LIVE Auth v2 session for this refresh cookie".
 * For a v1-only user that is expected (they have no v2 session at all), so
 * the SessionManager cross-checks the v1 refresh path before acting on a
 * 'dead' verdict from this helper. */
export async function classifyMasterQuotechRefresh(
  deps: SilentMasterRefreshDeps = {},
): Promise<RefreshOutcome> {
  const fetchImpl =
    deps.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined)
  if (!fetchImpl) return 'transient'
  // BUG-2026-08-03-AUTHV2-COOKIE-REFRESH-401-STORM: HttpOnly refresh cannot
  // use the v1 token tombstone. Once the server has definitively rejected
  // the cookie-path refresh, refuse further POSTs until login/success clears
  // the latch — otherwise SessionManager's near-expiry tick re-storms 401s
  // while the access JWT is still valid.
  if (deps.fetchImpl === undefined && isAuthV2CookieRefreshDead()) {
    return 'dead'
  }
  const url = deps.refreshUrl ?? QUOTECH_AUTHV2_REFRESH_URL
  const result = await requestMasterRefresh(
    fetchImpl,
    url,
    deps.fetchImpl === undefined,
  )
  if (result.ok) {
    if (deps.fetchImpl === undefined) clearAuthV2CookieRefreshDead()
    return 'refreshed'
  }
  if (result.status === 401 || result.status === 403) {
    if (deps.fetchImpl === undefined) markAuthV2CookieRefreshDead()
    return 'dead'
  }
  // 5xx / 429 / network throw / anything else — server hiccup or unreachable
  // master, not a dead credential.
  return 'transient'
}
